import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import Modal from '@/ui/Modal/Modal';
import Button from '@/ui/Button/Button';
import './ConfirmDialog.css';

const ConfirmContext = createContext(null);

// Imperative confirmation, a styled drop-in for window.confirm:
//   const confirm = useConfirm();
//   if (await confirm({ title, body, keyword: 'ELIMINAR' })) { …destructive… }
// Pass `keyword` to require type-to-confirm (the confirm button stays disabled
// until the typed text matches exactly). Reserve it for destructive deletes.
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { opts, seq } | null
  const resolverRef = useRef(null);
  const seqRef = useRef(0);

  const settle = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    resolve?.(result);
  }, []);

  const confirm = useCallback((opts = {}) => {
    seqRef.current += 1;
    return new Promise((resolve) => {
      // One dialog at a time: settle any prior call so awaiters cannot hang
      // forever when a later confirm() replaces the UI.
      const prev = resolverRef.current;
      resolverRef.current = resolve;
      setState({ opts, seq: seqRef.current });
      prev?.(false);
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <ConfirmDialog
          // Remount per call so the type-to-confirm input starts empty.
          key={state.seq}
          {...state.opts}
          onCancel={() => settle(false)}
          onConfirm={() => settle(true)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

ConfirmProvider.propTypes = {
  children: PropTypes.node,
};

export function ConfirmDialog({
  title = '¿Confirmar acción?',
  body,
  keyword,
  confirmLabel = 'Eliminar',
  cancelLabel = 'Cancelar',
  // Botón de confirmación: rojo por defecto (deletes destructivos). Acciones
  // irreversibles NO destructivas (p. ej. enviar cotizaciones) pasan 'seal'.
  confirmVariant = 'danger-solid',
  confirmDisabled = false,
  flat = false,
  onConfirm,
  onCancel,
}) {
  const [typed, setTyped] = useState('');
  const needsKeyword = Boolean(keyword);
  const matches = !needsKeyword || typed.trim() === keyword;

  return (
    <Modal open onClose={onCancel} size="small" flat={flat}>
      <Modal.Header title={title} onClose={onCancel} />
      <Modal.Body>
        {/* `div` (no `p`): el body puede ser un nodo estructurado (resumen con
            dl/filas), no solo texto. */}
        {body && <div className="confirm-body">{body}</div>}
        {needsKeyword && (
          <label className="confirm-type">
            <span className="confirm-type__lbl">
              Escribe <b>{keyword}</b> para confirmar.
            </span>
            <input
              className="confirm-type__input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck="false"
              aria-label={`Escribe ${keyword} para confirmar`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && matches && !confirmDisabled) {
                  onConfirm();
                }
              }}
            />
          </label>
        )}
      </Modal.Body>
      <Modal.Foot>
        <Button variant="ghost" size="md" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button
          variant={confirmVariant}
          size="md"
          disabled={!matches || confirmDisabled}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </Modal.Foot>
    </Modal>
  );
}

ConfirmDialog.propTypes = {
  title: PropTypes.string,
  body: PropTypes.node,
  keyword: PropTypes.string,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  confirmVariant: PropTypes.string,
  confirmDisabled: PropTypes.bool,
  flat: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
};

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}

export default ConfirmDialog;
